import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { HtmlOverlay3D } from '@asset-viz/objects/decor/HtmlOverlay3D'
import {
  SurfaceLineFeature3D,
  SurfaceLineSelectionType,
} from '../procedural/SurfaceLineFeature3D'
import {
  SurfaceMeshFeature3D,
  SurfaceMeshSelectionType,
} from '../procedural/SurfaceMeshFeature3D'
import { useSurfaceProps } from '@asset-viz/objects/surfaces/util/SurfaceProvider'
import { useSurfaceSample } from '@asset-viz/objects/surfaces/util/useSurfaceSample'
import { HtmlOverlayPriority } from '@asset-viz/utils/helpers'
import { useAssetVizLocale } from '@asset-viz/providers/locale'
import React from 'react'
import {
  GeckoComponentCoords,
  GeckoComponentPolygon,
  GeckoLegacyBoilerWall,
} from 'types'

/**
 * A component used to represent a wall on the surface of a boiler walls component
 * @param model             The boiler wall model
 * @param tiltNameLabel     Whether to tilt the name label or not
 * @param tiltRuler         Whether to tilt the bottom ruler or not
 * @constructor
 */
export const LegacyBoilerWallFeature3D: React.FC<{
  model: GeckoLegacyBoilerWall
  tiltNameLabel: boolean
  tiltRuler: boolean
}> = ({ model, tiltNameLabel, tiltRuler }) => {
  const { componentModel } = useSurfaceProps()
  const { showFeatures, showFeatureLabels } = useAssetVizInternal()
  const { path, customerBounds, namePlateCoords, tubeRangeCoords } =
    React.useMemo(() => {
      const nominal = model.wallNominalThickness
      const name = model.name
      const namePlateCoords = GeckoComponentCoords.CreateFromMeasurements(
        model.customerGeometry.componentID,
        model.geometry.start.x.plus(model.geometry.end.x).scale(0.5),
        model.geometry.end.y
      )
      const tubeRangeCoords = GeckoComponentCoords.CreateFromMeasurements(
        model.geometry.componentID,
        model.geometry.start.x.plus(model.geometry.end.x).scale(0.5),
        model.geometry.start.y
      )
      const path = GeckoComponentPolygon.CreateFromRect(model.geometry)

      const customerBounds = model.customerGeometry
      return {
        path: path,
        nominal: nominal,
        name: name,
        customerBounds: customerBounds,
        namePlateCoords: namePlateCoords,
        tubeRangeCoords: tubeRangeCoords,
      }
    }, [model])

  const { position: namePlatePosition } = useSurfaceSample(namePlateCoords)
  const { position: tubeRangePosition } = useSurfaceSample(tubeRangeCoords)
  const { formatMeasurementRange } = useAssetVizLocale()

  const showThisFeatureLabel = React.useMemo(
    () =>
      typeof showFeatureLabels === 'boolean'
        ? showFeatureLabels
        : showFeatureLabels({ feature: model, componentModel: componentModel }),
    [model, componentModel, showFeatureLabels]
  )

  const showThisFeature = React.useMemo(
    () =>
      typeof showFeatures === 'boolean'
        ? showFeatures
        : showFeatures({ feature: model, componentModel: componentModel }),
    [model, componentModel, showFeatures]
  )

  return (
    <group>
      {/* Name plate ------------------------------------------------------- */}
      {!namePlatePosition ? null : (
        <HtmlOverlay3D
          x={namePlatePosition.x}
          y={namePlatePosition.y}
          z={namePlatePosition.z}
          anchor={tiltNameLabel ? 'right' : 'bottom'}
          tilt={tiltNameLabel}
          priority={HtmlOverlayPriority.MajorFeatureNamePlate}
          visible={showThisFeatureLabel && showThisFeature}
          maxPixelOverlap={8}>
          <div style={{ padding: '0.5rem' }}>
            <h4>{model.name}</h4>
          </div>
        </HtmlOverlay3D>
      )}
      {/* Bottom ruler ----------------------------------------------------- */}
      {!tubeRangePosition ? null : (
        <HtmlOverlay3D
          x={tubeRangePosition.x}
          y={tubeRangePosition.y}
          z={tubeRangePosition.z}
          anchor={tiltRuler ? 'left' : 'top'}
          tilt={tiltRuler}
          priority={HtmlOverlayPriority.MajorFeatureNamePlate}
          visible={showThisFeatureLabel && showThisFeature}
          maxPixelOverlap={8}>
          <div style={{ padding: '0.5rem' }}>
            {model.geometry.width.num <= 32 ? null : 'Tubes '}
            {formatMeasurementRange({
              measurement: customerBounds.x,
              variant: 'geometric',
            })}
          </div>
        </HtmlOverlay3D>
      )}

      <SurfaceLineFeature3D
        path={path}
        featureModel={model}
        lineSelectionType={SurfaceLineSelectionType.FeatureLine}
      />

      <SurfaceMeshFeature3D
        path={path}
        featureModel={model}
        selectionType={SurfaceMeshSelectionType.VizToolTipSelectable}
        userDataName={'wall'}
      />
    </group>
  )
}
